import React, { useContext, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Box, Button, makeStyles } from '@material-ui/core';
import { useParams } from 'react-router';
import CheckCircleOutlineOutlinedIcon from '@material-ui/icons/CheckCircleOutlineOutlined';
import { useLatestClosureDate } from 'utils/closureHooks';
import { colors } from 'theme';
import { useExpeditionBuyerConfirmationByExpeditionId } from 'hooks';
import {
  Event,
  Expedition,
  ExpeditionBuyerConfirmationForm,
  ExpeditionBuyerConfirmationMetaData,
  ExpeditionClientPortion,
  ExpeditionRepartitionTonnageRepreneurFormData,
  UserRole,
} from 'types';
import BottomButtonBar from 'components/Generic/BottomButtonBar';
import { AppContext } from 'context';
import { notify } from 'components/Notifier';
import {
  formatExpeditionBuyerConfirmation,
  formatExpeditionRepartitionAccepteeRepreneurBatchPost,
} from 'utils/normalizer';
import {
  postExpeditionBuyerConfirmation,
  postExpeditionRepartitionAccepteeRepreneurBatch,
  putExpeditionBuyerConfirmation,
} from 'services/apiOperations';
import { transformObjectId } from 'services/apiPlatformTransformer';
import ExpeditionBuyerConfirmationCreateForm from 'components/Forms/ExpeditionBuyerConfirmationCreateForm';
import { computeRepartitionPercentageFromWeight } from 'services/expedition';
import ExpeditionRepartitionTonnageRepreneurForm from 'components/Forms/ExpeditionRepartitionAccepteeRepreneurForm';
import Loading from 'components/Loading';
import { Routes } from '../../routes';
import { floatWeightToInt } from '../../utils/weight';

const useStyles = makeStyles({
  buyerConfirmationTab: {
    display: 'flex',
    maxWidth: '90%',
    justifyContent: 'space-between',
    marginBottom: '36px',
  },
  fillInButton: {
    backgroundColor: colors.grey900,
    width: '135px',
    height: '38px',
    color: colors.white,
    '&:disabled': {
      color: colors.white,
      backgroundColor: colors.hoveredBlue,
    },
    fontWeight: 600,
    fontSize: '14px',
    borderRadius: 0,
    marginTop: 28,
  },
  linkNotify: {
    fontWeight: 900,
    color: 'white',
  },
});

type ExpeditionBuyerConfirmationTabProps = {
  expedition: Expedition;
  setCurrentTonnageAccepteInTons: (current: number) => void;
  setCurrentTonnageDecoteInTons: (current: number) => void;
  currentTonnageAccepteInTons: number;
  currentTonnageDecoteInTons: number;
};

const ExpeditionBuyerConfirmationTab: React.FC<ExpeditionBuyerConfirmationTabProps> = ({
  expedition,
  currentTonnageDecoteInTons,
  currentTonnageAccepteInTons,
  setCurrentTonnageDecoteInTons,
  setCurrentTonnageAccepteInTons,
}) => {
  const formNameTonnageAccepte = 'expeditionBuyerConfirmationForm';
  const formNameRepartitionAccepteeRepreneur = 'expeditionRepartitionAccepteeRepreneurForm';
  const [formName, setFormName] = useState(formNameTonnageAccepte);
  const classes = useStyles();
  const { t } = useTranslation();
  const { t: pt } = useTranslation('translation', { keyPrefix: 'expeditions.show.buyerConfirmationTab' });
  const context = useContext(AppContext);
  const sortingCenter = context.getSortingCenter?.();
  const roles = context.getRoles?.();
  const canUpdateRepartitionAccepteeRepreneur =
    roles &&
    (roles.includes(UserRole.ROLE_MANAGER) ||
      roles.includes(UserRole.ROLE_ADMIN) ||
      roles.includes(UserRole.ROLE_SUPPORT));
  const [expeditionClientPortionList, setExpeditionClientPortionList] = useState<ExpeditionClientPortion[]>([]);
  const [expeditionClientPortionWeightPercentLabels, setExpeditionClientPortionWeightPercentLabels] = useState<{
    [key: string]: string;
  }>({});
  const { expeditionId } = useParams<{ expeditionId: string }>();
  const expeditionDate = expedition ? new Date(expedition.expeditionDate) : null;
  const { value: expeditionBuyerConfirmations } = useExpeditionBuyerConfirmationByExpeditionId(expeditionId);
  const expeditionBuyerConfirmation = expeditionBuyerConfirmations ? expeditionBuyerConfirmations[0] : null;
  const readOnly = !sortingCenter?.isActive || (roles && roles.includes(UserRole.ROLE_CONSULTATION));
  const [disabledSaveButton, setDisabledSaveButton] = useState(false);
  const expeditionApiId = expeditionId ? `/api/expeditions/${expeditionId}` : '';
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentConfirmationDate, setCurrentConfirmationDate] = useState<Date | null>(null);
  const [latestClosureDate] = useLatestClosureDate(sortingCenter);
  const [isAutoTonnageRepris, setIsAutoTonnageRepris] = useState(false);

  const [expeditionBuyerConfirmationMetaData, setExpeditionBuyerConfirmationMetaData] =
    useState<ExpeditionBuyerConfirmationMetaData>({
      formValues: {
        confirmationDate: expeditionBuyerConfirmation ? expeditionBuyerConfirmation.confirmationDate : null,
        weight: expeditionBuyerConfirmation ? expeditionBuyerConfirmation.weight / 1000 : null,
        decote: expeditionBuyerConfirmation ? expeditionBuyerConfirmation.decote / 1000 : null,
      },
      isEditMode: false,
      isEditRepartitionAccepteeRepreneurMode: false,
    });

  useEffect(() => {
    if (!expeditionBuyerConfirmation) {
      return;
    }

    setExpeditionClientPortionList(
      expedition.expeditionClientPortions.sort((portionA, portionB) =>
        portionA.client.shortName.localeCompare(portionB.client.shortName),
      ),
    );
  }, [expedition, expeditionBuyerConfirmation]);

  useEffect(() => {
    if (!expedition || 1 > expeditionClientPortionList.length) return;

    const weightInTons: { [key: string]: string } = {};
    expeditionClientPortionList.forEach(
      (expeditionClientPortion, index) => (weightInTons[index] = expeditionClientPortion.weight.toString()),
    );

    computeRepartitionPercentageFromWeight(expedition.weight, weightInTons, 4).then(data => {
      setExpeditionClientPortionWeightPercentLabels(data?.repartitionEnPourcentage?.['hydra:member'] ?? []);
    });
  }, [expedition, expeditionClientPortionList]);

  useEffect(() => {
    setExpeditionBuyerConfirmationMetaData(previousMetaData => ({
      ...previousMetaData,
      formValues: {
        decote: expeditionBuyerConfirmation ? expeditionBuyerConfirmation.decote / 1000 : null,
        weight: expeditionBuyerConfirmation ? expeditionBuyerConfirmation.weight / 1000 : null,
        confirmationDate: expeditionBuyerConfirmation ? expeditionBuyerConfirmation.confirmationDate : null,
      },
    }));
  }, [expeditionBuyerConfirmation]);

  useEffect(() => {
    if (
      currentTonnageAccepteInTons > 0 &&
      Number(expedition.weight) <
        floatWeightToInt(currentTonnageDecoteInTons) + floatWeightToInt(currentTonnageAccepteInTons) &&
      (expeditionBuyerConfirmationMetaData.isEditMode ||
        expeditionBuyerConfirmationMetaData.isEditRepartitionAccepteeRepreneurMode)
    ) {
      notify(
        {
          message: pt('warning.tonnageReceveid', {}),
          severity: 'warning',
          width: 'small',
        },
        false,
      );
    } else if (
      expeditionBuyerConfirmationMetaData.isEditMode ||
      expeditionBuyerConfirmationMetaData.isEditRepartitionAccepteeRepreneurMode
    ) {
      notify(null);
    }
  }, [currentTonnageDecoteInTons, currentTonnageAccepteInTons]);

  const handleClickEditButton = () => {
    setExpeditionBuyerConfirmationMetaData(previousMetaData => ({
      ...previousMetaData,
      isEditMode: true,
    }));
    setFormName(formNameTonnageAccepte);
  };

  const handleClickEditRepartitionAccepteeRepreneurButton = () => {
    setExpeditionBuyerConfirmationMetaData(previousMetaData => ({
      ...previousMetaData,
      isEditRepartitionAccepteeRepreneurMode: true,
    }));
    setFormName(formNameRepartitionAccepteeRepreneur);
  };

  const handleClickCancelButton = () => {
    setExpeditionBuyerConfirmationMetaData(previousMetaData => ({
      ...previousMetaData,
      formValues: {
        decote: expeditionBuyerConfirmation ? expeditionBuyerConfirmation.decote / 1000 : null,
        weight: expeditionBuyerConfirmation ? expeditionBuyerConfirmation.weight / 1000 : null,
        confirmationDate: expeditionBuyerConfirmation ? expeditionBuyerConfirmation.confirmationDate : null,
      },
      isEditMode: false,
      isEditRepartitionAccepteeRepreneurMode: false,
    }));
    setIsAutoTonnageRepris(false);
  };

  const onSubmit = async (expeditionConfirmation: ExpeditionBuyerConfirmationForm) => {
    if (isSubmitting) {
      return;
    }

    const sortingCenterId = context.getSortingCenter?.()?.['@id'] ?? null;

    if (null === sortingCenterId) {
      return;
    }

    const formattedExpedition = formatExpeditionBuyerConfirmation(
      expeditionConfirmation,
      sortingCenterId,
      expeditionApiId,
    );
    setIsSubmitting(true);

    const expeditionBuyerConfirmationId = transformObjectId(expeditionBuyerConfirmation);
    const action = expeditionBuyerConfirmationId
      ? putExpeditionBuyerConfirmation(formattedExpedition, expeditionBuyerConfirmationId)
      : postExpeditionBuyerConfirmation(formattedExpedition);

    try {
      await action;
      notify({
        message: pt('success'),
        severity: 'success',
      });
      PubSub.publish(Event.REFRESH_EXPEDITION_BUYER_CONFIRMATION);
      setExpeditionBuyerConfirmationMetaData(previousMetaData => {
        const newMetaData: ExpeditionBuyerConfirmationMetaData = {
          ...previousMetaData,
          formValues: {
            decote: expeditionConfirmation.decote,
            confirmationDate: expeditionConfirmation.confirmationDate,
            weight: expeditionConfirmation.weight,
          },
          isEditMode: false,
        };
        return newMetaData;
      });
    } catch (error) {
      notify({
        message: t('errorGeneric'),
        severity: 'error',
      });
    }
    setIsSubmitting(false);
  };

  const handleSubmitRepartitionAccepteeRepreneur = async (
    expeditionRepartitionTonnageRepreneurFormData: ExpeditionRepartitionTonnageRepreneurFormData,
  ) => {
    if (isSubmitting) {
      return;
    }

    const sortingCenterId = context.getSortingCenter?.()?.['@id'] ?? null;

    if (null === sortingCenterId) {
      return;
    }

    setIsSubmitting(true);
    const formattedPostData = formatExpeditionRepartitionAccepteeRepreneurBatchPost(
      expeditionRepartitionTonnageRepreneurFormData,
      sortingCenterId,
      expedition.expeditionDate,
    );

    const weight = currentTonnageAccepteInTons ?? expeditionBuyerConfirmationMetaData.formValues?.weight ?? 0;
    const confirmationDate = new Date(
      currentConfirmationDate ?? expeditionBuyerConfirmationMetaData.formValues?.confirmationDate ?? new Date(),
    );
    const decote = currentTonnageDecoteInTons ?? expeditionBuyerConfirmationMetaData.formValues?.decote ?? 0;
    const expeditionConfirmation = {
      decote,
      weight,
      confirmationDate,
      formName: 'expeditionBuyerConfirmationForm',
    };

    const formattedExpedition = formatExpeditionBuyerConfirmation(
      expeditionConfirmation,
      sortingCenterId,
      expeditionApiId,
    );

    const expeditionBuyerConfirmationId = transformObjectId(expeditionBuyerConfirmation);

    try {
      await postExpeditionRepartitionAccepteeRepreneurBatch(formattedPostData);
      if (expeditionBuyerConfirmationId) {
        await putExpeditionBuyerConfirmation(formattedExpedition, expeditionBuyerConfirmationId, false);
      } else {
        await postExpeditionBuyerConfirmation(formattedExpedition, false);
      }
      if (expeditionRepartitionTonnageRepreneurFormData.isMiseEnCoherence) {
        if (latestClosureDate && latestClosureDate <= new Date(expedition.expeditionDate)) {
          notify({
            message: pt('miseEnCoherenceMoisNonClotureConfirmation'),
            severity: 'success',
          });
        } else {
          notify(
            {
              message: (
                <Trans
                  values={{
                    month: expeditionRepartitionTonnageRepreneurFormData.month,
                    year: expeditionRepartitionTonnageRepreneurFormData.year,
                  }}
                  i18nKey={
                    expeditionRepartitionTonnageRepreneurFormData.isOneClosure
                      ? 'expeditions.show.buyerConfirmationTab.info'
                      : 'expeditions.show.buyerConfirmationTab.infoMultiple'
                  }
                  // see ET-2546 for more informations
                  // eslint-disable-next-line jsx-a11y/anchor-has-content
                  components={[<a className={classes.linkNotify} href={Routes.Closures} />, <b> </b>]}
                />
              ),
              severity: 'info',
            },
            false,
          );
        }
      } else {
        notify({
          message: pt('successRepartitionAccepteeRepreneur'),
          severity: 'success',
        });
      }

      PubSub.publish(Event.REFRESH_EXPEDITION_BUYER_CONFIRMATION);
      setExpeditionBuyerConfirmationMetaData(previousMetaData => {
        const newMetaData: ExpeditionBuyerConfirmationMetaData = {
          ...previousMetaData,
          formValues: {
            decote: expeditionConfirmation.decote,
            confirmationDate: expeditionConfirmation.confirmationDate,
            weight: expeditionConfirmation.weight,
          },
          isEditRepartitionAccepteeRepreneurMode: false,
        };
        return newMetaData;
      });
    } catch (error) {
      notify({
        message: t('errorGeneric'),
        severity: 'error',
      });
    }

    setIsSubmitting(false);
  };

  return (
    <>
      <Box className={classes.buyerConfirmationTab}>
        <ExpeditionBuyerConfirmationCreateForm
          formName={formNameTonnageAccepte}
          onSubmit={onSubmit}
          expeditionDate={expeditionDate}
          editMode={
            expeditionBuyerConfirmationMetaData.isEditMode ||
            expeditionBuyerConfirmationMetaData.isEditRepartitionAccepteeRepreneurMode
          }
          resetFormValues={expeditionBuyerConfirmationMetaData.formValues}
          setCurrentTonnageAccepteInTons={setCurrentTonnageAccepteInTons}
          setCurrentConfirmationDate={setCurrentConfirmationDate}
          setIsAutoTonnageRepris={setIsAutoTonnageRepris}
          isAutoTonnageRepris={isAutoTonnageRepris}
          setCurrentTonnageDecoteInTons={setCurrentTonnageDecoteInTons}
        />
        {!readOnly && !expeditionBuyerConfirmation && (
          <Button
            className={classes.fillInButton}
            onClick={() => handleClickEditButton()}
            disabled={expeditionBuyerConfirmationMetaData.isEditMode}
          >
            {pt('fillInConfirmation')}
          </Button>
        )}
        {!readOnly && canUpdateRepartitionAccepteeRepreneur && expeditionBuyerConfirmation && (
          <Button
            className={classes.fillInButton}
            onClick={() => handleClickEditRepartitionAccepteeRepreneurButton()}
            disabled={expeditionBuyerConfirmationMetaData.isEditRepartitionAccepteeRepreneurMode}
          >
            {pt('updateRepartitionAccepteeRepreneur')}
          </Button>
        )}
        {(expeditionBuyerConfirmationMetaData.isEditMode ||
          expeditionBuyerConfirmationMetaData.isEditRepartitionAccepteeRepreneurMode) && (
          <BottomButtonBar
            previousStepLabel={t('expeditions.edit.cancelButtonLabel')}
            nextColor="default"
            nextStepLabel={t('expeditions.edit.editButtonLabel')}
            formName={formName}
            nextStepIcon={<CheckCircleOutlineOutlinedIcon />}
            onClickPrevious={() => handleClickCancelButton()}
            disableNext={disabledSaveButton}
          />
        )}
      </Box>
      {isSubmitting ? (
        <Loading />
      ) : (
        <ExpeditionRepartitionTonnageRepreneurForm
          expedition={expedition}
          formName={formNameRepartitionAccepteeRepreneur}
          expeditionBuyerConfirmation={expeditionBuyerConfirmation}
          expeditionClientPortionWeightPercentLabels={expeditionClientPortionWeightPercentLabels}
          onSubmit={handleSubmitRepartitionAccepteeRepreneur}
          isEditMode={expeditionBuyerConfirmationMetaData.isEditRepartitionAccepteeRepreneurMode}
          setDisabledSaveButton={setDisabledSaveButton}
          currentTonnageAccepteInTons={currentTonnageAccepteInTons}
          isAutoTonnageRepris={isAutoTonnageRepris}
          setIsAutoTonnageRepris={setIsAutoTonnageRepris}
        />
      )}
    </>
  );
};

export default ExpeditionBuyerConfirmationTab;
